// src/pages/StopGoContinue.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../config/firebase';
import {
  collection,
  addDoc,
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';

// =====================================================
// Topic configuration
// =====================================================
const TOPICS = [
  { id: 'cocosAthletics', label: 'Co-Curriculars & Athletics', blurb: 'CoCo offerings, teams, training, competition' },
  { id: 'resLife',        label: 'Student / Faculty Residential Life', blurb: 'Dorm life, duty, weekend coverage, faculty residences' },
  { id: 'academics',      label: 'Academics',                  blurb: 'Curriculum, instruction, pedagogy, rigor, courses' },
  { id: 'facultyCulture', label: 'Faculty Culture',            blurb: 'Collegiality, professional growth, communication, morale' },
  { id: 'dining',         label: 'Dining',                     blurb: 'Meals, sit-down lunch, menus, dining hall experience' },
  { id: 'mentalHealth',   label: 'Health & Wellness',          blurb: 'Counseling, wellbeing supports, awareness, prevention' },
  { id: 'behavior',       label: 'Student Behavior',           blurb: 'Discipline, expectations, accountability, restorative practice' },
  { id: 'weekends',       label: 'Weekends',                   blurb: 'Trips, on-campus programming, downtime, structure' },
  { id: 'arts',           label: 'Arts',                       blurb: 'Visual arts, music, theater, performance, exhibition' },
];

const COLUMNS = [
  { id: 'stop',     label: 'STOP',     blurb: 'Things we want to stop doing',                color: '#E83530' },
  { id: 'go',       label: 'GO',       blurb: 'New things we want to start doing',           color: '#2E7D32' },
  { id: 'continue', label: 'CONTINUE', blurb: 'Things we already do that we want to keep',   color: '#233C5F' },
];

const MIN_ITEMS_PER_COLUMN = 5;
const TOP_N = 5;

// =====================================================
// Brand
// =====================================================
const KA = {
  red:   '#E83530',
  navy:  '#233C5F',
  gold:  '#A29164',
  paper: '#FAF8F4',
  ink:   '#1A1A1A',
  mute:  '#6B6B6B',
  line:  '#E5E1D8',
};

// =====================================================
// Local-storage draft key (so a refresh doesn't wipe progress)
// =====================================================
const draftKey = (uid) => `sgc_draft_${uid || 'anon'}`;

const emptyResponses = () => {
  const out = {};
  TOPICS.forEach(t => {
    out[t.id] = { stop: [''], go: [''], continue: [''] };
  });
  return out;
};

const emptyTopPicks = () => {
  const out = {};
  TOPICS.forEach(t => {
    out[t.id] = { stop: [], go: [], continue: [] };
  });
  return out;
};

// =====================================================
// Component
// =====================================================
export default function StopGoContinue() {
  const { user } = useAuth();

  // phase: 'intro' | 'brainstorm' | 'rank' | 'review' | 'done'
  const [phase, setPhase] = useState('intro');

  // metadata
  const [tableNumber, setTableNumber] = useState('');

  // brainstorm state: responses[topicId][columnId] = [string, string, ...]
  const [responses, setResponses] = useState(emptyResponses());

  // rank state: topPicks[topicId][columnId] = [itemString, itemString, ...] (up to 5)
  const [topPicks, setTopPicks] = useState(emptyTopPicks());

  // current topic index for both brainstorm and rank phases
  const [topicIdx, setTopicIdx] = useState(0);

  // submit state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submittedId, setSubmittedId] = useState('');

  // autosave indicator: 'idle' | 'saving' | 'saved' | 'error'
  const [autosaveState, setAutosaveState] = useState('idle');
  const [hydrated, setHydrated] = useState(false);

  // Soft-warning modal for under-min items / under-5 picks
  // shape: { type: 'brainstorm' | 'rank' | 'submit', shortages: [{ col, count }] } | null
  const [warning, setWarning] = useState(null);

  // -----------------------------------------------------
  // Restore draft on mount
  //
  // Strategy:
  //   1. Read localStorage immediately (instant) so the user sees their
  //      work before the network round-trip.
  //   2. In parallel, fetch the Firestore draft. If the remote draft is
  //      newer (by updatedAt), prefer it. This handles the cross-device
  //      case: scribe started on iPad, switched to laptop.
  //   3. After hydration completes, allow the autosave effect to run.
  // -----------------------------------------------------
  useEffect(() => {
    if (!user?.uid) return;

    let cancelled = false;

    const applyDraft = (draft) => {
      if (!draft) return;
      if (draft.tableNumber) setTableNumber(draft.tableNumber);

      // Normalize against the CURRENT TOPICS/COLUMNS schema. Any stale
      // topic IDs (e.g. left over from a previous deploy where the topic
      // list was different) get silently dropped; any missing topics start
      // fresh. This makes the tool resilient to schema changes between
      // deploys — the alternative is crashing on render when the new code
      // accesses a topic ID that doesn't exist in the saved draft.
      if (draft.responses) {
        const fresh = emptyResponses();
        TOPICS.forEach(t => {
          if (draft.responses[t.id]) {
            COLUMNS.forEach(c => {
              if (Array.isArray(draft.responses[t.id][c.id])) {
                fresh[t.id][c.id] = draft.responses[t.id][c.id];
              }
            });
          }
        });
        setResponses(fresh);
      }
      if (draft.topPicks) {
        const fresh = emptyTopPicks();
        TOPICS.forEach(t => {
          if (draft.topPicks[t.id]) {
            COLUMNS.forEach(c => {
              if (Array.isArray(draft.topPicks[t.id][c.id])) {
                fresh[t.id][c.id] = draft.topPicks[t.id][c.id];
              }
            });
          }
        });
        setTopPicks(fresh);
      }

      if (draft.phase) setPhase(draft.phase);
      if (typeof draft.topicIdx === 'number') {
        // Clamp in case TOPICS shrank between deploys.
        setTopicIdx(Math.min(Math.max(draft.topicIdx, 0), TOPICS.length - 1));
      }
    };

    // 1. Local first
    let localDraft = null;
    try {
      const raw = localStorage.getItem(draftKey(user.uid));
      if (raw) localDraft = JSON.parse(raw);
    } catch (e) {
      // ignore corrupt draft
    }
    if (localDraft) applyDraft(localDraft);

    // 2. Then remote — overrides if newer
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'stopGoContinueDrafts', user.uid));
        if (cancelled) return;
        if (!snap.exists()) {
          setHydrated(true);
          return;
        }
        const remote = snap.data();
        const localTs = localDraft?.updatedAt || 0;
        const remoteTs = remote.updatedAtMs || 0;
        if (remoteTs > localTs) {
          applyDraft(remote);
        }
      } catch (e) {
        console.warn('Draft fetch failed', e);
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();

    return () => { cancelled = true; };
  }, [user?.uid]);

  // -----------------------------------------------------
  // Persist draft on change (localStorage immediately, Firestore debounced)
  // -----------------------------------------------------
  useEffect(() => {
    if (!user?.uid) return;
    if (!hydrated) return;          // don't write before initial load completes
    if (phase === 'done') return;

    const now = Date.now();
    const snapshot = {
      tableNumber,
      responses,
      topPicks,
      phase,
      topicIdx,
      updatedAt: now,
    };

    // Local: instant
    try {
      localStorage.setItem(draftKey(user.uid), JSON.stringify(snapshot));
    } catch (e) {
      // ignore quota
    }

    // Remote: debounce 1500ms so we don't write on every keystroke
    setAutosaveState('saving');
    const timer = setTimeout(async () => {
      try {
        await setDoc(
          doc(db, 'stopGoContinueDrafts', user.uid),
          {
            ...snapshot,
            updatedAtMs: now,
            updatedAt: serverTimestamp(),
            scribeUid: user.uid,
            scribeEmail: user.email || null,
            scribeName: user.displayName || null,
          }
        );
        setAutosaveState('saved');
      } catch (e) {
        console.warn('Draft autosave failed', e);
        setAutosaveState('error');
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [user?.uid, hydrated, tableNumber, responses, topPicks, phase, topicIdx]);

  // -----------------------------------------------------
  // Brainstorm handlers
  // -----------------------------------------------------
  const updateItem = (topicId, colId, idx, value) => {
    setResponses(prev => {
      const next = { ...prev };
      const col = [...next[topicId][colId]];
      col[idx] = value;
      next[topicId] = { ...next[topicId], [colId]: col };
      return next;
    });
  };

  const addItem = (topicId, colId) => {
    setResponses(prev => {
      const next = { ...prev };
      next[topicId] = {
        ...next[topicId],
        [colId]: [...next[topicId][colId], ''],
      };
      return next;
    });
  };

  const removeItem = (topicId, colId, idx) => {
    setResponses(prev => {
      const next = { ...prev };
      const col = next[topicId][colId].filter((_, i) => i !== idx);
      next[topicId] = {
        ...next[topicId],
        [colId]: col.length ? col : [''],
      };
      return next;
    });
  };

  // For the current topic, how many non-empty items per column?
  const filledCounts = useMemo(() => {
    const out = {};
    TOPICS.forEach(t => {
      out[t.id] = {};
      COLUMNS.forEach(c => {
        out[t.id][c.id] = (responses[t.id]?.[c.id] || []).filter(s => s.trim()).length;
      });
    });
    return out;
  }, [responses]);

  const topicComplete = (tid) =>
    COLUMNS.every(c => (filledCounts[tid]?.[c.id] || 0) >= MIN_ITEMS_PER_COLUMN);

  const completedTopicsCount = useMemo(
    () => TOPICS.filter(t => topicComplete(t.id)).length,
    [filledCounts] // eslint-disable-line
  );

  // -----------------------------------------------------
  // Ranking handlers
  // -----------------------------------------------------
  const togglePick = (topicId, colId, item) => {
    setTopPicks(prev => {
      const next = { ...prev };
      const current = next[topicId][colId];
      let updated;
      if (current.includes(item)) {
        updated = current.filter(x => x !== item);
      } else if (current.length < TOP_N) {
        updated = [...current, item];
      } else {
        updated = current; // at cap
      }
      next[topicId] = { ...next[topicId], [colId]: updated };
      return next;
    });
  };

  const movePick = (topicId, colId, idx, dir) => {
    setTopPicks(prev => {
      const next = { ...prev };
      const arr = [...next[topicId][colId]];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      next[topicId] = { ...next[topicId], [colId]: arr };
      return next;
    });
  };

  // Brainstormed (non-empty, trimmed) items for a topic+column.
  const brainstormedItems = (tid, cid) =>
    (responses[tid]?.[cid] || []).map(s => s.trim()).filter(Boolean);

  // A column needs ranking ONLY if the group brainstormed more than TOP_N
  // items in it. With ≤5 items, every item is automatically a "top pick" —
  // there's nothing to prioritize.
  const columnNeedsRanking = (tid, cid) =>
    brainstormedItems(tid, cid).length > TOP_N;

  // A topic only needs the ranking screen if at least one of its columns
  // needs ranking. Otherwise we silently auto-fill and skip past it.
  const topicNeedsRanking = (tid) =>
    COLUMNS.some(c => columnNeedsRanking(tid, c.id));

  // A topic's ranking is "complete" when every column-that-needs-ranking
  // has exactly TOP_N picks. Columns that don't need ranking are exempt
  // (their picks are auto-filled, see autoFillTopPicksForTopic below).
  const rankingTopicComplete = (tid) =>
    COLUMNS.every(c => {
      if (!columnNeedsRanking(tid, c.id)) return true;
      return (topPicks[tid]?.[c.id] || []).length === TOP_N;
    });

  const completedRankingsCount = useMemo(
    () => TOPICS.filter(t => rankingTopicComplete(t.id)).length,
    [topPicks] // eslint-disable-line
  );

  // For columns where the group typed ≤5 items, copy those items straight
  // into topPicks so they end up in the final submission. Idempotent —
  // safe to call repeatedly. Run when entering the rank phase and again
  // each time the rank topic changes (in case the scribe went back to
  // brainstorm and added/removed items).
  const autoFillTopPicksForTopic = (tid) => {
    setTopPicks(prev => {
      const next = { ...prev };
      const topicPicks = { ...(next[tid] || { stop: [], go: [], continue: [] }) };
      let changed = false;
      COLUMNS.forEach(c => {
        if (!columnNeedsRanking(tid, c.id)) {
          // Auto-fill with the brainstormed items in their typed order.
          const items = brainstormedItems(tid, c.id);
          const current = topicPicks[c.id] || [];
          // Only rewrite if different (avoid extra renders / autosave churn).
          if (
            current.length !== items.length ||
            current.some((v, i) => v !== items[i])
          ) {
            topicPicks[c.id] = items;
            changed = true;
          }
        }
      });
      if (!changed) return prev;
      next[tid] = topicPicks;
      return next;
    });
  };

  // Find the index of the next topic (≥ startIdx) that actually needs the
  // ranking screen. Returns -1 if none do.
  const nextRankTopicIdx = (startIdx) => {
    for (let i = startIdx; i < TOPICS.length; i++) {
      if (topicNeedsRanking(TOPICS[i].id)) return i;
    }
    return -1;
  };

  // -----------------------------------------------------
  // Submit
  // -----------------------------------------------------

  // Gather submission-time shortages across ALL topics × columns.
  // A shortage = the group BRAINSTORMED fewer than MIN_ITEMS_PER_COLUMN
  // items in that column. (Ranking-phase shortages don't exist anymore:
  // columns with ≤5 brainstormed items are auto-filled and exempted, and
  // columns with >5 are hard-gated to require exactly 5 picks before
  // leaving the rank screen.)
  const getSubmissionShortages = () => {
    const out = [];
    TOPICS.forEach(t => {
      COLUMNS.forEach(c => {
        const count = brainstormedItems(t.id, c.id).length;
        if (count < MIN_ITEMS_PER_COLUMN) out.push({ topic: t, col: c, count });
      });
    });
    return out;
  };

  const handleSubmitClick = () => {
    setError('');
    if (!tableNumber.trim()) {
      setError('Table / group number is required.');
      return;
    }
    const shortages = getSubmissionShortages();
    if (shortages.length === 0) {
      performSubmit();
    } else {
      setWarning({
        type: 'submit',
        shortages,
        onProceed: performSubmit,
      });
    }
  };

  const performSubmit = async () => {
    setError('');
    // Clean responses: trim, drop blanks
    const cleanedResponses = {};
    TOPICS.forEach(t => {
      cleanedResponses[t.id] = {};
      COLUMNS.forEach(c => {
        cleanedResponses[t.id][c.id] = (responses[t.id]?.[c.id] || [])
          .map(s => s.trim())
          .filter(Boolean);
      });
    });

    setSubmitting(true);
    try {
      const docRef = await addDoc(collection(db, 'stopGoContinue'), {
        tableNumber: tableNumber.trim(),
        scribeUid: user?.uid || null,
        scribeEmail: user?.email || null,
        scribeName: user?.displayName || null,
        responses: cleanedResponses,
        topPicks,
        createdAt: serverTimestamp(),
        sessionLabel: 'Faculty PD',
      });
      setSubmittedId(docRef.id);
      // clear local draft
      try { localStorage.removeItem(draftKey(user?.uid)); } catch (e) {}
      // clear remote draft (best-effort; non-blocking)
      try {
        if (user?.uid) {
          await deleteDoc(doc(db, 'stopGoContinueDrafts', user.uid));
        }
      } catch (e) {
        console.warn('Draft cleanup failed', e);
      }
      setPhase('done');
    } catch (e) {
      console.error('SGC submit failed', e);
      setError(e.message || 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // -----------------------------------------------------
  // Render
  // -----------------------------------------------------
  if (!user) {
    return (
      <div style={styles.shell}>
        <div style={styles.card}>
          <h2 style={{ color: KA.navy, fontFamily: 'Georgia, serif', margin: 0 }}>
            Please sign in
          </h2>
          <p style={{ color: KA.mute }}>
            You need to be signed in to your King’s Academy account to use this tool.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.shell}>
      <Header autosaveState={autosaveState} phase={phase} />
      {phase === 'intro'      && renderIntro()}
      {phase === 'brainstorm' && renderBrainstorm()}
      {phase === 'rank'       && renderRank()}
      {phase === 'review'     && renderReview()}
      {phase === 'done'       && renderDone()}
      {warning && (
        <WarningModal
          warning={warning}
          onCancel={() => setWarning(null)}
          onProceed={() => {
            const proceed = warning.onProceed;
            setWarning(null);
            if (proceed) proceed();
          }}
        />
      )}
    </div>
  );

  // -----------------------------------------------------
  // INTRO
  // -----------------------------------------------------
  function renderIntro() {
    return (
      <div style={styles.card}>
        <h1 style={styles.h1}>Stop / Go / Continue</h1>
        <p style={styles.lead}>
          A facilitated group brainstorm. Each group elects a scribe — that’s you.
          You’ll capture the group’s ideas across <b>9 topics</b>, with at least <b>5 items</b>{' '}
          for each of <span style={{ color: KA.red, fontWeight: 700 }}>Stop</span>,
          {' '}<span style={{ color: '#2E7D32', fontWeight: 700 }}>Go</span>, and
          {' '}<span style={{ color: KA.navy, fontWeight: 700 }}>Continue</span>.
          After all topics are filled in, you’ll work with your group to pick the
          {' '}<b>top {TOP_N}</b> in each column for each topic.
        </p>

        <div style={{ ...styles.field, maxWidth: 380 }}>
          <label style={styles.label}>Table / Group Number</label>
          <input
            type="text"
            value={tableNumber}
            onChange={(e) => setTableNumber(e.target.value)}
            placeholder="e.g. 4"
            style={styles.input}
          />
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button
            style={styles.primaryBtn}
            disabled={!tableNumber.trim()}
            onClick={() => setPhase('brainstorm')}
          >
            Begin Brainstorm →
          </button>
        </div>

        <div style={styles.tipBox}>
          <b>Tip:</b> Your progress saves automatically in this browser. If you accidentally
          refresh, your work is still here.
        </div>
      </div>
    );
  }

  // -----------------------------------------------------
  // BRAINSTORM
  // -----------------------------------------------------
  function renderBrainstorm() {
    const topic = TOPICS[topicIdx];
    const counts = filledCounts[topic.id];
    const canAdvance = topicComplete(topic.id);
    const isLast = topicIdx === TOPICS.length - 1;

    // Compute shortages — which columns are under the minimum.
    const shortages = COLUMNS
      .map(c => ({ col: c, count: counts[c.id] || 0 }))
      .filter(s => s.count < MIN_ITEMS_PER_COLUMN);

    const advance = () => {
      if (isLast) {
        // Entering the rank phase. Auto-fill picks for every column that
        // has ≤5 items across ALL topics (so it's done in one shot), then
        // jump to the first topic that actually needs ranking.
        TOPICS.forEach(t => autoFillTopPicksForTopic(t.id));
        const firstRankIdx = nextRankTopicIdx(0);
        if (firstRankIdx === -1) {
          // Nothing to rank — straight to review.
          setPhase('review');
        } else {
          setTopicIdx(firstRankIdx);
          setPhase('rank');
        }
      } else {
        setTopicIdx(topicIdx + 1);
      }
      window.scrollTo({ top: 0 });
    };

    const onNext = () => {
      if (canAdvance) {
        advance();
      } else {
        setWarning({
          type: 'brainstorm',
          shortages,
          onProceed: advance,
        });
      }
    };

    return (
      <div style={styles.card}>
        <ProgressBar
          completed={completedTopicsCount}
          total={TOPICS.length}
          label={`Brainstorm progress · ${completedTopicsCount} of ${TOPICS.length} topics complete`}
        />

        <div style={styles.topicHeader}>
          <div>
            <div style={styles.topicEyebrow}>
              Topic {topicIdx + 1} of {TOPICS.length}
            </div>
            <h2 style={styles.h2}>{topic.label}</h2>
            <div style={styles.topicBlurb}>{topic.blurb}</div>
          </div>
        </div>

        <div style={styles.threeCol}>
          {COLUMNS.map(col => {
            const items = responses?.[topic.id]?.[col.id] || [''];
            const filled = counts?.[col.id] || 0;
            const reached = filled >= MIN_ITEMS_PER_COLUMN;
            return (
              <div key={col.id} style={styles.column}>
                <div style={{
                  ...styles.colHeader,
                  background: col.color,
                }}>
                  <span style={{ fontWeight: 800, letterSpacing: 1 }}>{col.label}</span>
                  <span style={{
                    ...styles.colCount,
                    background: reached ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.18)',
                  }}>
                    {filled} / {MIN_ITEMS_PER_COLUMN}+
                  </span>
                </div>
                <div style={styles.colBlurb}>{col.blurb}</div>

                {items.map((val, i) => (
                  <div key={i} style={styles.itemRow}>
                    <span style={styles.itemNum}>{i + 1}.</span>
                    <textarea
                      value={val}
                      onChange={(e) => updateItem(topic.id, col.id, i, e.target.value)}
                      placeholder="Type an idea…"
                      rows={2}
                      style={styles.itemInput}
                    />
                    <button
                      type="button"
                      onClick={() => removeItem(topic.id, col.id, i)}
                      style={styles.removeBtn}
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => addItem(topic.id, col.id)}
                  style={styles.addBtn}
                >
                  + Add another
                </button>
              </div>
            );
          })}
        </div>

        <div style={styles.navRow}>
          <button
            style={styles.secondaryBtn}
            onClick={() => {
              if (topicIdx === 0) setPhase('intro');
              else setTopicIdx(topicIdx - 1);
              window.scrollTo({ top: 0 });
            }}
          >
            ← Back
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {!canAdvance && (
              <span style={{ color: KA.mute, fontSize: 13 }}>
                Goal: {MIN_ITEMS_PER_COLUMN}+ items in each column.
              </span>
            )}
            <button
              style={styles.primaryBtn}
              onClick={onNext}
            >
              {isLast ? 'Continue to Prioritization →' : 'Next Topic →'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // -----------------------------------------------------
  // RANK
  // -----------------------------------------------------
  function renderRank() {
    const topic = TOPICS[topicIdx];
    const canAdvance = rankingTopicComplete(topic.id);

    // Determine the next topic that needs ranking (or -1 if none remain).
    const nextIdx = nextRankTopicIdx(topicIdx + 1);
    const isLastRank = nextIdx === -1;

    // Safety: if the user navigates onto a topic that doesn't need
    // ranking (e.g. coming back from brainstorm where they trimmed items
    // down to ≤5), auto-fill and skip forward. Done in an effect so
    // we don't call setState during render.
    if (!topicNeedsRanking(topic.id)) {
      // Defer to next tick.
      setTimeout(() => {
        autoFillTopPicksForTopic(topic.id);
        if (isLastRank) {
          setPhase('review');
        } else {
          setTopicIdx(nextIdx);
        }
      }, 0);
      return (
        <div style={styles.card}>
          <div style={{ padding: 40, textAlign: 'center', color: KA.mute }}>
            Skipping — this topic doesn’t need ranking.
          </div>
        </div>
      );
    }

    const advance = () => {
      if (isLastRank) {
        setPhase('review');
      } else {
        setTopicIdx(nextIdx);
      }
      window.scrollTo({ top: 0 });
    };

    // No soft-warning modal here — the under-min issue was already raised
    // during brainstorm, and the scribe can't add new items on this screen.
    // Just disable Next until ranking is complete for columns that need it.

    return (
      <div style={styles.card}>
        <ProgressBar
          completed={completedRankingsCount}
          total={TOPICS.length}
          label={`Prioritization · ${completedRankingsCount} of ${TOPICS.length} topics ranked`}
          color={KA.gold}
        />

        <div style={styles.topicHeader}>
          <div>
            <div style={styles.topicEyebrow}>
              Prioritization · Topic {topicIdx + 1} of {TOPICS.length}
            </div>
            <h2 style={styles.h2}>{topic.label}</h2>
            <div style={styles.topicBlurb}>
              As a group, pick your top {TOP_N} in each column that needs it. Tap to select,
              then use the arrows to put picks in priority order. Columns with {TOP_N} or fewer
              items are carried through automatically.
            </div>
          </div>
        </div>

        <div style={styles.threeCol}>
          {COLUMNS.map(col => {
            const all = (responses[topic.id]?.[col.id] || [])
              .map(s => s.trim())
              .filter(Boolean);
            const picks = topPicks[topic.id]?.[col.id] || [];
            const needsRank = columnNeedsRanking(topic.id, col.id);

            // Auto-included column (≤5 brainstormed) — show what was
            // brainstormed as a read-only carry-through, no ranking UI.
            if (!needsRank) {
              return (
                <div key={col.id} style={styles.column}>
                  <div style={{ ...styles.colHeader, background: col.color }}>
                    <span style={{ fontWeight: 800, letterSpacing: 1 }}>{col.label}</span>
                    <span style={{
                      ...styles.colCount,
                      background: 'rgba(255,255,255,0.25)',
                    }}>
                      Auto
                    </span>
                  </div>
                  <div style={styles.colBlurb}>
                    Only {all.length} item{all.length === 1 ? '' : 's'} — all carried through, no ranking needed.
                  </div>
                  <div style={{ padding: '10px' }}>
                    {all.length === 0 ? (
                      <div style={{ color: KA.mute, fontSize: 13, fontStyle: 'italic', padding: 8 }}>
                        (nothing brainstormed)
                      </div>
                    ) : (
                      all.map((item, i) => (
                        <div key={i} style={{
                          ...styles.pickRow,
                          borderBottom: i === all.length - 1 ? 'none' : `1px solid ${KA.line}`,
                        }}>
                          <div style={styles.pickRank}>{i + 1}</div>
                          <div style={styles.pickText}>{item}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            }

            // Ranking column — full picker UI.
            return (
              <div key={col.id} style={styles.column}>
                <div style={{ ...styles.colHeader, background: col.color }}>
                  <span style={{ fontWeight: 800, letterSpacing: 1 }}>{col.label}</span>
                  <span style={{
                    ...styles.colCount,
                    background: picks.length === TOP_N ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.18)',
                  }}>
                    {picks.length} / {TOP_N}
                  </span>
                </div>

                {/* Picks (ordered) */}
                {picks.length > 0 && (
                  <div style={styles.picksBox}>
                    <div style={styles.picksLabel}>Top {TOP_N} (in order)</div>
                    {picks.map((item, i) => (
                      <div key={item} style={styles.pickRow}>
                        <div style={styles.pickRank}>{i + 1}</div>
                        <div style={styles.pickText}>{item}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <button
                            style={styles.arrowBtn}
                            onClick={() => movePick(topic.id, col.id, i, -1)}
                            disabled={i === 0}
                            title="Move up"
                          >▲</button>
                          <button
                            style={styles.arrowBtn}
                            onClick={() => movePick(topic.id, col.id, i, +1)}
                            disabled={i === picks.length - 1}
                            title="Move down"
                          >▼</button>
                        </div>
                        <button
                          style={styles.removeBtn}
                          onClick={() => togglePick(topic.id, col.id, item)}
                          title="Remove from picks"
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pool */}
                <div style={styles.poolLabel}>
                  {picks.length < TOP_N
                    ? `Tap to add (${TOP_N - picks.length} more needed)`
                    : 'All picks made. Tap an item below to swap it in.'}
                </div>
                <div style={styles.pool}>
                  {all.map(item => {
                    const selected = picks.includes(item);
                    const atCap = picks.length >= TOP_N && !selected;
                    return (
                      <button
                        type="button"
                        key={item}
                        onClick={() => togglePick(topic.id, col.id, item)}
                        disabled={atCap}
                        style={{
                          ...styles.poolItem,
                          background: selected ? col.color : 'white',
                          color: selected ? 'white' : KA.ink,
                          borderColor: selected ? col.color : KA.line,
                          opacity: atCap ? 0.4 : 1,
                          cursor: atCap ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {item}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div style={styles.navRow}>
          <button
            style={styles.secondaryBtn}
            onClick={() => {
              // Find the previous topic that also needed ranking. If none,
              // go back into brainstorm at the last topic.
              let prevRankIdx = -1;
              for (let i = topicIdx - 1; i >= 0; i--) {
                if (topicNeedsRanking(TOPICS[i].id)) { prevRankIdx = i; break; }
              }
              if (prevRankIdx === -1) {
                setTopicIdx(TOPICS.length - 1);
                setPhase('brainstorm');
              } else {
                setTopicIdx(prevRankIdx);
              }
              window.scrollTo({ top: 0 });
            }}
          >
            ← Back
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {!canAdvance && (
              <span style={{ color: KA.mute, fontSize: 13 }}>
                Pick {TOP_N} in each column to continue.
              </span>
            )}
            <button
              style={{
                ...styles.primaryBtn,
                opacity: canAdvance ? 1 : 0.4,
                cursor: canAdvance ? 'pointer' : 'not-allowed',
              }}
              disabled={!canAdvance}
              onClick={advance}
            >
              {isLastRank ? 'Review & Submit →' : 'Next Topic →'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // -----------------------------------------------------
  // REVIEW
  // -----------------------------------------------------
  function renderReview() {
    return (
      <div style={styles.card}>
        <h2 style={styles.h2}>Review & Submit</h2>
        <p style={{ color: KA.mute, marginTop: 0 }}>
          Table <b>{tableNumber}</b> · Scribe: <b>{user?.displayName || user?.email}</b>
        </p>

        {TOPICS.map(t => (
          <div key={t.id} style={styles.reviewTopic}>
            <h3 style={styles.h3}>{t.label}</h3>
            <div style={styles.reviewGrid}>
              {COLUMNS.map(c => (
                <div key={c.id}>
                  <div style={{ ...styles.reviewColHeader, color: c.color }}>
                    {c.label} · Top {TOP_N}
                  </div>
                  <ol style={styles.reviewList}>
                    {(topPicks[t.id]?.[c.id] || []).map((item, i) => (
                      <li key={i} style={styles.reviewItem}>{item}</li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </div>
        ))}

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.navRow}>
          <button
            style={styles.secondaryBtn}
            onClick={() => {
              // Find the LAST topic that actually needs ranking. If none
              // do (every column was ≤5), go back to brainstorm instead.
              let lastRankIdx = -1;
              for (let i = TOPICS.length - 1; i >= 0; i--) {
                if (topicNeedsRanking(TOPICS[i].id)) { lastRankIdx = i; break; }
              }
              if (lastRankIdx === -1) {
                setTopicIdx(TOPICS.length - 1);
                setPhase('brainstorm');
              } else {
                setTopicIdx(lastRankIdx);
                setPhase('rank');
              }
              window.scrollTo({ top: 0 });
            }}
          >
            {TOPICS.some(t => topicNeedsRanking(t.id))
              ? '← Back to ranking'
              : '← Back to brainstorm'}
          </button>
          <button
            style={{
              ...styles.primaryBtn,
              background: submitting ? KA.mute : KA.red,
            }}
            disabled={submitting}
            onClick={handleSubmitClick}
          >
            {submitting ? 'Submitting…' : 'Submit Responses'}
          </button>
        </div>
      </div>
    );
  }

  // -----------------------------------------------------
  // DONE
  // -----------------------------------------------------
  function renderDone() {
    return (
      <div style={styles.card}>
        <h2 style={{ ...styles.h2, color: KA.red }}>Submitted. Thank you!</h2>
        <p style={{ color: KA.ink }}>
          Your group’s responses have been saved. Submission ID:
          {' '}<code style={styles.code}>{submittedId}</code>
        </p>
        <p style={{ color: KA.mute }}>
          You can close this tab. If your group needs to submit a second entry,
          refresh the page to start over.
        </p>
      </div>
    );
  }
}

// =====================================================
// Sub-components
// =====================================================
function Header({ autosaveState, phase }) {
  const showStatus = phase !== 'intro' && phase !== 'done';
  const label = {
    idle:   '',
    saving: 'Saving…',
    saved:  'Saved',
    error:  'Save failed — your work is still here locally',
  }[autosaveState] || '';
  const color = {
    idle:   KA.mute,
    saving: KA.mute,
    saved:  '#2E7D32',
    error:  KA.red,
  }[autosaveState] || KA.mute;

  return (
    <div style={styles.header}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <img src="/kings_logo.png" alt="" style={{ height: 36 }} />
        <div>
          <div style={{ fontFamily: 'Georgia, serif', color: KA.navy, fontSize: 18, fontWeight: 700 }}>
            Stop · Go · Continue
          </div>
          <div style={{ color: KA.mute, fontSize: 12 }}>Faculty PD · Madaba, Jordan</div>
        </div>
      </div>
      {showStatus && label && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          color,
          fontWeight: 600,
        }}>
          <span style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: color,
            opacity: autosaveState === 'saving' ? 0.5 : 1,
          }} />
          {label}
        </div>
      )}
    </div>
  );
}

// =====================================================
// Warning modal: shown when scribe tries to advance / submit
// without hitting the minimum item count.
// =====================================================
function WarningModal({ warning, onCancel, onProceed }) {
  const { type, shortages } = warning;

  // Group shortages for the submit case (which spans all topics).
  const isSubmit = type === 'submit';

  // For brainstorm/rank: shortages is [{ col, count }]
  // For submit:           shortages is [{ topic, col, count }]
  return (
    <div style={modalStyles.backdrop} onClick={onCancel}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={modalStyles.title}>
          {isSubmit ? 'Some columns have fewer than 5 picks' : 'Heads up — under the goal'}
        </h3>
        <p style={modalStyles.body}>
          {type === 'brainstorm' && (
            <>The expectation is at least <b>5 items</b> per column. You can keep brainstorming, or proceed anyway if your group has run dry.</>
          )}
          {type === 'rank' && (
            <>The expectation is <b>5 picks</b> per column. You can go back and add more, or proceed anyway if your group only has a few.</>
          )}
          {type === 'submit' && (
            <>You can still submit, but a few columns didn’t reach 5 picks. Here’s what’s short:</>
          )}
        </p>

        <div style={modalStyles.list}>
          {isSubmit ? (
            // group shortages by topic for readability
            (() => {
              const byTopic = {};
              shortages.forEach(s => {
                if (!byTopic[s.topic.id]) byTopic[s.topic.id] = { label: s.topic.label, rows: [] };
                byTopic[s.topic.id].rows.push(s);
              });
              return Object.values(byTopic).map((g, i) => (
                <div key={i} style={{ marginBottom: 10 }}>
                  <div style={modalStyles.topicHeader}>{g.label}</div>
                  {g.rows.map((s, j) => (
                    <div key={j} style={modalStyles.row}>
                      <span style={{ ...modalStyles.pill, background: s.col.color }}>{s.col.label}</span>
                      <span>{s.count} / 5</span>
                    </div>
                  ))}
                </div>
              ));
            })()
          ) : (
            shortages.map((s, i) => (
              <div key={i} style={modalStyles.row}>
                <span style={{ ...modalStyles.pill, background: s.col.color }}>{s.col.label}</span>
                <span>{s.count} / 5</span>
              </div>
            ))
          )}
        </div>

        <div style={modalStyles.actions}>
          <button style={modalStyles.cancelBtn} onClick={onCancel}>
            Go back & add more
          </button>
          <button style={modalStyles.proceedBtn} onClick={onProceed}>
            {isSubmit ? 'Submit Anyway' : 'Proceed Anyway'}
          </button>
        </div>
      </div>
    </div>
  );
}

const modalStyles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 16,
  },
  modal: {
    background: 'white',
    borderRadius: 10,
    padding: 24,
    maxWidth: 480,
    width: '100%',
    boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
    maxHeight: '85vh',
    overflowY: 'auto',
  },
  title: {
    fontFamily: 'Georgia, serif',
    color: KA.navy,
    margin: '0 0 8px',
    fontSize: 20,
  },
  body: {
    color: KA.ink,
    fontSize: 14,
    lineHeight: 1.5,
    margin: '0 0 14px',
  },
  list: {
    background: KA.paper,
    borderRadius: 6,
    padding: '10px 12px',
    marginBottom: 16,
    fontSize: 13,
  },
  topicHeader: {
    fontSize: 12,
    fontWeight: 700,
    color: KA.navy,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: 4,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '4px 0',
  },
  pill: {
    color: 'white',
    fontWeight: 700,
    fontSize: 11,
    padding: '2px 8px',
    borderRadius: 10,
    letterSpacing: 0.6,
  },
  actions: {
    display: 'flex',
    gap: 10,
    justifyContent: 'flex-end',
  },
  cancelBtn: {
    background: 'white',
    color: KA.navy,
    border: `1px solid ${KA.navy}`,
    padding: '9px 16px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  proceedBtn: {
    background: KA.red,
    color: 'white',
    border: 'none',
    padding: '9px 16px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
};

function ProgressBar({ completed, total, label, color = KA.red }) {
  const pct = (completed / total) * 100;
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 13, color: KA.mute, fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 13, color: KA.mute }}>{Math.round(pct)}%</span>
      </div>
      <div style={{
        height: 8,
        background: KA.line,
        borderRadius: 4,
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`,
          height: '100%',
          background: color,
          transition: 'width 200ms ease',
        }} />
      </div>
    </div>
  );
}

// =====================================================
// Styles
// =====================================================
const styles = {
  shell: {
    minHeight: '100vh',
    background: KA.paper,
    padding: '24px 16px 80px',
    fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
    color: KA.ink,
  },
  header: {
    maxWidth: 1100,
    margin: '0 auto 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  card: {
    maxWidth: 1100,
    margin: '0 auto',
    background: 'white',
    borderRadius: 12,
    padding: 28,
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: `1px solid ${KA.line}`,
  },
  h1: {
    fontFamily: 'Georgia, serif',
    color: KA.navy,
    margin: '0 0 12px',
    fontSize: 32,
  },
  h2: {
    fontFamily: 'Georgia, serif',
    color: KA.navy,
    margin: '0 0 4px',
    fontSize: 24,
  },
  h3: {
    fontFamily: 'Georgia, serif',
    color: KA.navy,
    margin: '0 0 8px',
    fontSize: 18,
  },
  lead: {
    color: KA.ink,
    fontSize: 15,
    lineHeight: 1.5,
  },
  field: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 },
  label: { fontSize: 12, color: KA.mute, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' },
  input: {
    padding: '10px 12px',
    border: `1px solid ${KA.line}`,
    borderRadius: 6,
    fontSize: 16,
    fontFamily: 'inherit',
    outline: 'none',
  },
  primaryBtn: {
    background: KA.red,
    color: 'white',
    border: 'none',
    padding: '11px 20px',
    borderRadius: 6,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  secondaryBtn: {
    background: 'white',
    color: KA.navy,
    border: `1px solid ${KA.navy}`,
    padding: '10px 18px',
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  tipBox: {
    marginTop: 24,
    padding: '10px 14px',
    background: '#FFF8EC',
    border: `1px solid ${KA.gold}`,
    color: KA.ink,
    borderRadius: 6,
    fontSize: 13,
  },
  topicHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: 18,
    gap: 16,
  },
  topicEyebrow: {
    color: KA.gold,
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
  },
  topicBlurb: { color: KA.mute, fontSize: 13, marginTop: 4 },
  threeCol: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 16,
  },
  column: {
    border: `1px solid ${KA.line}`,
    borderRadius: 8,
    background: '#FDFCFA',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  colHeader: {
    color: 'white',
    padding: '10px 14px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 14,
  },
  colCount: {
    fontSize: 12,
    padding: '2px 8px',
    borderRadius: 12,
    color: 'white',
    fontWeight: 600,
  },
  colBlurb: {
    padding: '8px 14px',
    color: KA.mute,
    fontSize: 12,
    borderBottom: `1px solid ${KA.line}`,
  },
  itemRow: {
    display: 'flex',
    gap: 6,
    padding: '8px 10px 0',
    alignItems: 'flex-start',
  },
  itemNum: {
    fontSize: 12,
    color: KA.mute,
    fontWeight: 700,
    minWidth: 18,
    paddingTop: 8,
  },
  itemInput: {
    flex: 1,
    padding: '8px 10px',
    border: `1px solid ${KA.line}`,
    borderRadius: 6,
    fontSize: 14,
    fontFamily: 'inherit',
    resize: 'vertical',
    outline: 'none',
    minHeight: 38,
  },
  removeBtn: {
    background: 'transparent',
    border: 'none',
    color: KA.mute,
    fontSize: 20,
    cursor: 'pointer',
    padding: '0 6px',
    fontWeight: 700,
    lineHeight: 1,
  },
  addBtn: {
    margin: '10px',
    padding: '8px',
    background: 'transparent',
    border: `1px dashed ${KA.line}`,
    borderRadius: 6,
    color: KA.navy,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  navRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 28,
    paddingTop: 18,
    borderTop: `1px solid ${KA.line}`,
  },
  picksBox: {
    margin: '10px',
    padding: 10,
    background: 'white',
    border: `1px solid ${KA.line}`,
    borderRadius: 6,
  },
  picksLabel: {
    fontSize: 11,
    color: KA.mute,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 6,
  },
  pickRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 0',
    borderBottom: `1px solid ${KA.line}`,
  },
  pickRank: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    background: KA.gold,
    color: 'white',
    fontWeight: 700,
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  pickText: { flex: 1, fontSize: 14 },
  arrowBtn: {
    background: 'white',
    border: `1px solid ${KA.line}`,
    borderRadius: 3,
    width: 22,
    height: 18,
    cursor: 'pointer',
    fontSize: 10,
    color: KA.navy,
    padding: 0,
    lineHeight: 1,
  },
  poolLabel: {
    fontSize: 12,
    color: KA.mute,
    padding: '0 14px',
    fontWeight: 600,
  },
  pool: {
    padding: '8px 10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  poolItem: {
    textAlign: 'left',
    padding: '8px 10px',
    border: `1px solid ${KA.line}`,
    borderRadius: 6,
    fontSize: 13,
    fontFamily: 'inherit',
  },
  reviewTopic: {
    marginTop: 20,
    paddingTop: 16,
    borderTop: `1px solid ${KA.line}`,
  },
  reviewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 16,
    marginTop: 8,
  },
  reviewColHeader: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 1,
    marginBottom: 6,
  },
  reviewList: {
    margin: 0,
    paddingLeft: 20,
  },
  reviewItem: {
    fontSize: 13,
    marginBottom: 4,
    color: KA.ink,
  },
  error: {
    marginTop: 16,
    padding: '10px 14px',
    background: '#FFEBEE',
    border: `1px solid ${KA.red}`,
    color: KA.red,
    borderRadius: 6,
    fontSize: 13,
  },
  code: {
    background: KA.paper,
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: 12,
  },
};